import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Image as ImageIcon, Search } from 'lucide-react';
import { getAdminGallery, createGalleryItem, updateGalleryItem, deleteGalleryItem } from '../services/adminApi';
import FileUpload from '../components/FileUpload';

const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    type: 'image',
    location: '',
    category: 'general',
    featured: false
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const data = await getAdminGallery();
      setItems(data);
    } catch (error) {
      console.error('Failed to fetch gallery items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateGalleryItem(editingItem._id, formData);
      } else {
        await createGalleryItem(formData);
      }
      fetchItems();
      resetForm();
    } catch (error) {
      console.error('Failed to save gallery item:', error);
      alert('Failed to save gallery item: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (item) => {
    setFormData({
      url: item.url,
      title: item.title,
      type: item.type || 'image',
      location: item.location || '',
      category: item.category || 'general',
      featured: item.featured || false
    });
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await deleteGalleryItem(id);
        fetchItems();
      } catch (error) {
        console.error('Failed to delete gallery item:', error);
        alert('Failed to delete gallery item');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      url: '',
      title: '',
      type: 'image',
      location: '',
      category: 'general',
      featured: false
    });
    setEditingItem(null);
    setShowModal(false);
  };

  const filteredItems = items.filter(item =>
    (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-white text-center py-12">Loading gallery...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white mb-2">Gallery Manager</h1>
          <p className="text-gray-400">Manage homepage, main, and location galleries</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center space-x-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-6 py-3 rounded-lg transition-colors">
          <Plus size={20} />
          <span>New Image</span>
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input type="text" placeholder="Search by title or category..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredItems.map((item) => (
          <div key={item._id} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-yellow-400 transition-colors group relative">
            <img src={item.imageUrl} alt={item.title || 'Gallery image'} className="w-full h-48 object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
              <button onClick={() => handleEdit(item)} className="p-3 bg-blue-500 hover:bg-blue-600 rounded-lg text-white transition-colors">
                <Edit2 size={18} />
              </button>
              <button onClick={() => handleDelete(item._id)} className="p-3 bg-red-500 hover:bg-red-600 rounded-lg text-white transition-colors">
                <Trash2 size={18} />
              </button>
            </div>
            <div className="p-3">
              <p className="text-white text-sm font-medium truncate">{item.title || 'Untitled'}</p>
              <p className="text-gray-400 text-xs capitalize">{item.location || item.type}</p>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <ImageIcon className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">No gallery items found</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">{editingItem ? 'Edit Image' : 'Add New Image'}</h2>
              <button
                type="button"
                onClick={resetForm}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <FileUpload
                value={formData.url}
                onChange={(url) => setFormData({ ...formData, url: url })}
                type="images"
                accept="image/*"
                label="Gallery Image *"
              />
              <div>
                <label className="text-white font-medium mb-2 block">Title *</label>
                <input 
                  type="text" 
                  value={formData.title} 
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                  placeholder="Image title"
                  required
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    required
                  >
                    <option value="general">General Gallery</option>
                    <option value="new-era">New Era Gallery</option>
                  </select>
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Location</label>
                  <input 
                    type="text" 
                    value={formData.location} 
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })} 
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" 
                    placeholder="Optional location"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <input 
                  type="checkbox" 
                  id="featured"
                  checked={formData.featured} 
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} 
                  className="w-5 h-5 bg-gray-700 border border-gray-600 rounded text-yellow-400 focus:ring-yellow-400"
                />
                <label htmlFor="featured" className="text-white font-medium">Featured</label>
              </div>
              {formData.category === 'location' && (
                <div>
                  <label className="text-white font-medium mb-2 block">Location ID</label>
                  <input type="text" value={formData.locationId} onChange={(e) => setFormData({ ...formData, locationId: e.target.value })} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" placeholder="Location ID for location-specific images" />
                </div>
              )}
              <div className="flex justify-end space-x-4 pt-4">
                <button type="button" onClick={resetForm} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">Cancel</button>
                <button type="submit" className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg transition-colors">{editingItem ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;